import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AccountLayout from '@/components/feature/AccountLayout';
import PortfolioPreviewModal from '@/components/common/PortfolioPreviewModal';
import { Toast, ConfirmModal } from '@/components/common';
import { fetchMyJobPostingDetail, fetchPublicJobPostingDetail, closeJobPosting, submitJobPostingAudit } from '@/service/api/job';
import { useBusinessCategoryStore } from '@/stores/businessCategoryStore';
import JobApplicationsModal from '../modules/JobApplicationsModal';

/** 格式化日期为 YYYY-MM-DD */
function formatDateShort(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** 就业类型映射 */
const employmentTypeMap: Record<string, string> = {
  FULL_TIME: '全职',
  PART_TIME: '兼职',
  INTERNSHIP: '实习',
  PROJECT: '项目外包',
};

/** 薪资周期映射 */
const salaryPeriodMap: Record<string, string> = {
  MONTHLY: '月',
  YEARLY: '年',
  DAILY: '日',
  HOURLY: '小时',
  PROJECT: '次',
};

/** 格式化薪资展示文本 */
function formatJobSalaryDisplay(
  salaryMin?: string,
  salaryMax?: string,
  salaryType?: string,
  salaryPeriod?: string,
  salaryCurrency: string = 'CNY'
): string {
  const periodText = salaryPeriodMap[salaryPeriod || 'MONTHLY'] || salaryPeriod || '月';

  if (salaryType === 'NEGOTIABLE' || (!salaryMin && !salaryMax)) {
    return '薪资面议';
  }

  if (salaryType === 'FIXED' || (salaryMin && !salaryMax)) {
    return `${salaryMin} ${salaryCurrency} / ${periodText}`;
  }

  if (salaryMin && salaryMax) {
    return `${salaryMin} - ${salaryMax} ${salaryCurrency} / ${periodText}`;
  }

  return '薪资面议';
}

/** 状态样式配置 */
function getStatusBadgeInfo(status?: string) {
  const s = (status || '').toUpperCase();
  if (s === 'PENDING_REVIEW' || s === 'PENDING' || s === 'IN_AUDIT') {
    return { label: '审核中', bg: 'bg-amber-50 text-amber-700 border-amber-200', type: 'in_audit' };
  }
  if (s === 'REJECTED' || s === 'AUDIT_REJECTED') {
    return { label: '已驳回', bg: 'bg-red-50 text-red-600 border-red-200', type: 'rejected' };
  }
  if (s === 'RECRUITING' || s === 'PUBLISHED' || s === 'APPROVED') {
    return { label: '招聘中', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', type: 'published' };
  }
  if (s === 'DRAFT') {
    return { label: '草稿', bg: 'bg-slate-100 text-slate-600 border-slate-200', type: 'draft' };
  }
  if (s === 'CLOSED') {
    return { label: '已关闭', bg: 'bg-slate-100 text-slate-500 border-slate-200', type: 'closed' };
  }
  if (s === 'COMPLETED') {
    return { label: '已完成', bg: 'bg-emerald-50 text-emerald-800 border-emerald-300', type: 'completed' };
  }
  return { label: status || '未知', bg: 'bg-slate-100 text-slate-500 border-slate-200', type: 'other' };
}

export default function MyJobPostingDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [job, setJob] = useState<Api.Job.JobResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 查看申请列表模态框控制
  const [showApplicationsModal, setShowApplicationsModal] = useState(false);

  // Toast & Confirm Modal 状态
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isSubmittingAudit, setIsSubmittingAudit] = useState(false);

  // 媒体预览 modal 状态
  const [previewMedia, setPreviewMedia] = useState<{
    open: boolean;
    title?: string;
    intro?: string;
    isVideo?: boolean;
    mediaUrl?: string | null;
  }>({
    open: false,
    title: '',
    intro: '',
    isVideo: false,
    mediaUrl: null,
  });

  const { categories, fetchCategories, getCategoryNameByCode } = useBusinessCategoryStore();
  const categoriesFetchedRef = useRef(false);

  useEffect(() => {
    if (categoriesFetchedRef.current || categories.length > 0) return;
    categoriesFetchedRef.current = true;
    fetchCategories().catch((err) => {
      console.error('获取分类失败:', err);
      categoriesFetchedRef.current = false;
    });
  }, [categories.length, fetchCategories]);

  const loadJobDetail = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);

    try {
      // 优先请求企业自己的招聘详情
      try {
        const res = await fetchMyJobPostingDetail(id);
        if (res && res.id) {
          setJob(res);
          return;
        }
      } catch {
        // 尝试备选公开获取
        const publicRes = await fetchPublicJobPostingDetail(id);
        if (publicRes && publicRes.id) {
          setJob(publicRes);
          return;
        }
      }
      setError('无法获取招聘岗位管理详情，该岗位可能已被删除或无访问权限');
    } catch (err: any) {
      console.error('获取岗位管理详情失败:', err);
      setError(err?.message || '获取招聘岗位管理详情失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobDetail();
  }, [id]);

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setToast({ message, type });
  };

  // 关闭招聘岗位
  const handleConfirmClose = async () => {
    if (!id) return;
    setIsClosing(true);
    try {
      await closeJobPosting(id);
      showToast('已成功关闭该招聘岗位', 'success');
      setShowCloseConfirm(false);
      loadJobDetail();
    } catch (err: any) {
      console.error('关闭招聘岗位失败:', err);
      showToast(err?.message || '关闭招聘岗位失败', 'error');
    } finally {
      setIsClosing(false);
    }
  };

  // 提交审核
  const handleSubmitAudit = async () => {
    if (!id) return;
    setIsSubmittingAudit(true);
    try {
      await submitJobPostingAudit(id);
      showToast('已成功提交审核', 'success');
      loadJobDetail();
    } catch (err: any) {
      console.error('提交审核失败:', err);
      showToast(err?.message || '提交审核失败', 'error');
    } finally {
      setIsSubmittingAudit(false);
    }
  };

  const handlePreviewImage = (img: Api.Job.DemandAttachmentResponse) => {
    if (!img.access_url) return;
    const isVid = (img.mime_type || '').startsWith('video/');
    setPreviewMedia({
      open: true,
      title: img.display_name || img.file_name || '',
      intro: undefined,
      isVideo: isVid,
      mediaUrl: img.access_url,
    });
  };

  if (loading) {
    return (
      <AccountLayout
        activePath="/my/job-postings"
        breadcrumbs={[{ label: '账号中心', href: '/profile' }, { label: '招聘管理', href: '/my/job-postings' }, { label: '岗位详情' }]}
      >
        <div className="py-20 bg-white rounded-2xl border border-slate-100 text-center">
          <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-xs text-slate-400">正在加载岗位管理详情...</p>
        </div>
      </AccountLayout>
    );
  }

  if (error || !job) {
    return (
      <AccountLayout
        activePath="/my/job-postings"
        breadcrumbs={[{ label: '账号中心', href: '/profile' }, { label: '招聘管理', href: '/my/job-postings' }, { label: '岗位详情' }]}
      >
        <div className="py-16 bg-white rounded-2xl border border-slate-100 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
            <i className="ri-error-warning-line text-2xl text-red-500"></i>
          </div>
          <h3 className="text-base font-bold text-slate-900 mb-1">未找到岗位信息</h3>
          <p className="text-xs text-slate-500 mb-6">{error || '岗位可能已被移除或无权访问'}</p>
          <Link
            to="/my/job-postings"
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-semibold transition-all inline-flex items-center gap-1.5"
          >
            <i className="ri-arrow-left-line"></i> 返回招聘岗位管理
          </Link>
        </div>
      </AccountLayout>
    );
  }

  const badgeInfo = getStatusBadgeInfo(job.status);
  const categoryNames =
    job.category_codes && job.category_codes.length > 0
      ? job.category_codes.map((code) => getCategoryNameByCode(code, code))
      : ['通用'];

  const empTypeLabel = employmentTypeMap[job.employment_type || ''] || job.employment_type || '全职';
  const salaryText = formatJobSalaryDisplay(
    job.salary_min,
    job.salary_max,
    job.salary_type,
    job.salary_period,
    job.salary_currency
  );

  return (
    <AccountLayout
      activePath="/my/job-postings"
      breadcrumbs={[
        { label: '账号中心', href: '/profile' },
        { label: '招聘管理', href: '/my/job-postings' },
        { label: job.title || '岗位详情' },
      ]}
    >
      <div className="space-y-6">
        {/* 顶部标题与管理按钮栏 */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
            <div className="flex items-center gap-3">
              <Link
                to="/my/job-postings"
                className="w-8 h-8 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-all"
                title="返回招聘管理"
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </Link>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium border ${badgeInfo.bg}`}>
                    {badgeInfo.label}
                  </span>
                  {categoryNames.map((cat, idx) => (
                    <span key={idx} className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-purple-50 text-purple-700 border border-purple-200">
                      {cat}
                    </span>
                  ))}
                  <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    {empTypeLabel}
                  </span>
                </div>
                <h1 className="text-xl font-bold text-slate-900 mt-1.5">{job.title}</h1>
              </div>
            </div>

            {/* 操作控制区 */}
            <div className="flex items-center gap-2.5 flex-wrap shrink-0">
              {/* RECRUITING / CLOSED / COMPLETED 均可【查看申请列表】 */}
              {(badgeInfo.type === 'published' || badgeInfo.type === 'closed' || badgeInfo.type === 'completed') && (
                <button
                  type="button"
                  onClick={() => setShowApplicationsModal(true)}
                  className="px-4 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 shadow-md shadow-primary-500/20"
                >
                  <i className="ri-file-user-line text-sm"></i>
                  查看申请列表
                </button>
              )}

              {/* RECRUITING 状态允许【关闭招聘岗位】 */}
              {badgeInfo.type === 'published' && (
                <button
                  type="button"
                  onClick={() => setShowCloseConfirm(true)}
                  className="px-4 py-2 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-stop-circle-line"></i>
                  关闭招聘
                </button>
              )}

              {/* 草稿状态：允许提交审核与继续编辑 */}
              {badgeInfo.type === 'draft' && (
                <>
                  <button
                    type="button"
                    disabled={isSubmittingAudit}
                    onClick={handleSubmitAudit}
                    className="px-4 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-all cursor-pointer whitespace-nowrap shadow-md shadow-primary-500/20 disabled:opacity-50 flex items-center gap-1"
                  >
                    {isSubmittingAudit ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
                    提交审核
                  </button>
                  <Link
                    to={`/post-job?id=${job.id}`}
                    className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                  >
                    继续编辑
                  </Link>
                </>
              )}

              {/* 已驳回状态：修改并重新提交 */}
              {badgeInfo.type === 'rejected' && (
                <Link
                  to={`/post-job?id=${job.id}`}
                  className="px-4 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-all cursor-pointer whitespace-nowrap shadow-md shadow-primary-500/20"
                >
                  修改并重新提交
                </Link>
              )}
            </div>
          </div>

          {/* 属性信息面板 */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs text-slate-600 bg-slate-50/80 p-4 rounded-xl border border-slate-100">
            <div>
              <span className="text-slate-400 block mb-0.5">薪资待遇</span>
              <span className="text-base font-bold text-emerald-600">{salaryText}</span>
            </div>
            <div>
              <span className="text-slate-400 block mb-0.5">工作地点</span>
              <span className="font-semibold text-slate-800">{job.city_name || '全国/远程'}</span>
            </div>
            <div>
              <span className="text-slate-400 block mb-0.5">发布时间</span>
              <span className="font-medium text-slate-800">{formatDateShort(job.published_at) || '未发布'}</span>
            </div>
            <div>
              <span className="text-slate-400 block mb-0.5">截止时间</span>
              <span className="font-medium text-slate-800 flex items-center gap-1">
                {formatDateShort(job.deadline_at) || '长期有效'}
                {job.deadline_at && new Date(job.deadline_at).getTime() < Date.now() && (
                  <span className="text-[11px] text-amber-600 font-normal">(已过截止时间)</span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* 详细内容列表卡片 */}
        <div className="space-y-6">
          {/* 岗位描述 */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-3">
            <h3 className="font-bold text-slate-900 text-sm border-b border-slate-100 pb-2.5 flex items-center gap-2">
              <i className="ri-file-text-line text-primary-600 text-base"></i>
              岗位描述
            </h3>
            <div className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">
              {job.job_description || '暂无详细描述信息。'}
            </div>
          </div>

          {/* 岗位要求 */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-3">
            <h3 className="font-bold text-slate-900 text-sm border-b border-slate-100 pb-2.5 flex items-center gap-2">
              <i className="ri-task-line text-primary-600 text-base"></i>
              岗位要求
            </h3>
            <div className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">
              {job.job_requirements || '暂无详细要求。'}
            </div>
          </div>

          {/* 技能要求 */}
          {job.skill_requirements && (
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-3">
              <h3 className="font-bold text-slate-900 text-sm border-b border-slate-100 pb-2.5 flex items-center gap-2">
                <i className="ri-tools-line text-primary-600 text-base"></i>
                技能要求
              </h3>
              <div className="flex flex-wrap gap-2">
                {job.skill_requirements
                  .split('|')
                  .map((skill) => skill.trim())
                  .filter(Boolean)
                  .map((skill, index) => (
                    <span
                      key={index}
                      className="px-2.5 py-1 bg-slate-50 text-slate-700 rounded-lg text-xs font-medium border border-slate-200/60"
                    >
                      {skill}
                    </span>
                  ))}
              </div>
            </div>
          )}

          {/* 福利待遇 */}
          {job.benefits && (
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-3">
              <h3 className="font-bold text-slate-900 text-sm border-b border-slate-100 pb-2.5 flex items-center gap-2">
                <i className="ri-gift-line text-primary-600 text-base"></i>
                相关福利
              </h3>
              <div className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">
                {job.benefits}
              </div>
            </div>
          )}

          {/* 企业展现图集 */}
          {job.enterprise_images && job.enterprise_images.length > 0 && (
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-3">
              <h3 className="font-bold text-slate-900 text-sm border-b border-slate-100 pb-2.5 flex items-center gap-2">
                <i className="ri-image-line text-primary-600 text-base"></i>
                企业环境与资质
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {job.enterprise_images.map((img, idx) => (
                  <div
                    key={idx}
                    onClick={() => handlePreviewImage(img)}
                    className={`relative rounded-xl overflow-hidden min-h-[120px] max-h-[180px] bg-slate-100 border border-slate-200 group transition-all flex items-center justify-center ${img.access_url ? 'cursor-pointer hover:shadow-md hover:border-primary-300' : 'cursor-not-allowed'
                      }`}
                  >
                    {img.access_url ? (
                      <>
                        <img
                          src={img.access_url}
                          alt={img.display_name || img.file_name || '资质环境图'}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs p-2 text-center">
                          <i className="ri-search-2-line text-lg mr-1"></i> 预览
                        </div>
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">{img.display_name || '未上传图片'}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 查看申请列表模态框 */}
      {showApplicationsModal && (
        <JobApplicationsModal
          job={job}
          onClose={() => setShowApplicationsModal(false)}
          showToast={showToast}
        />
      )}

      {/* 确认关闭招聘弹窗 */}
      <ConfirmModal
        open={showCloseConfirm}
        title="关闭招聘岗位"
        content="确定要关闭该招聘岗位吗？关闭后该岗位将不再对外展示，但您仍可以审核处理已有求职投递申请。"
        type="danger"
        confirmText="确定关闭"
        cancelText="取消"
        loading={isClosing}
        onConfirm={handleConfirmClose}
        onCancel={() => setShowCloseConfirm(false)}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* 媒体预览 Modal */}
      <PortfolioPreviewModal
        open={previewMedia.open}
        title={previewMedia.title}
        intro={previewMedia.intro}
        isVideo={previewMedia.isVideo}
        mediaUrl={previewMedia.mediaUrl}
        onClose={() => setPreviewMedia((prev) => ({ ...prev, open: false }))}
      />
    </AccountLayout>
  );
}
